import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ROLE_LABEL, type DiaryEntry, type Lang } from '@buildiary/core';
import { LANG_NAME, LANGS } from '../i18n';
import { request } from '../lib/api';
import { longDay, time } from '../lib/format';
import { toWebHref } from '../lib/hrefs';
import { useAsync } from '../lib/useAsync';
import { BrandTile, Icon, Wordmark, type IconName } from '../ui/Icon';
import { Avatar, Dropdown, FormDialog, Sheet, s, useToast } from '../ui/kit';
import { useApp, useLang, useT } from './context';
import { canOpenPath, findItem, landingFor, visibleMenus, type Menu } from './nav';
import { isNative, nativeForget } from '../lib/native';

/** Routes that draw their own phone header and footer (no top bar, no tab bar on phones). */
const PHONE_FLOWS = ['/stores/receive', '/projects/diary'];

export function Shell() {
  const app = useApp();
  const loc = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 1100 && window.innerWidth >= 768);
  const [palette, setPalette] = useState(false);
  const [adding, setAdding] = useState(false);
  useEffect(() => setDrawer(false), [loc.pathname]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);
  const flow = app.isPhone && PHONE_FLOWS.some((p) => loc.pathname === p);
  const t = useT();
  return (
    <div className={`shell${collapsed && !app.isPhone ? ' collapsed' : ''}${drawer ? ' drawer-open' : ''}`}>
      <a className="skip-link" href="#main">{t('nav.skip')}</a>
      <div className="sb-col">
        <Sidebar collapsed={collapsed && !app.isPhone} onClose={() => setDrawer(false)} />
      </div>
      {drawer && <div className="scrim" onClick={() => setDrawer(false)} aria-hidden="true" />}
      <div className="main-col">
        {!flow && (app.isPhone ? <PhoneBar onMenu={() => setDrawer(true)} onSearch={() => setPalette(true)} /> : <TopBar collapsed={collapsed} onCollapse={() => setCollapsed(!collapsed)} onSearch={() => setPalette(true)} />)}
        {!flow && <PlanBanner />}
        <main id="main" className={`content${flow ? ' flush' : ''}`} tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      {app.isPhone && !flow && <TabBar onAdd={() => setAdding(true)} onMore={() => setDrawer(true)} />}
      {adding && <AddSheet onClose={() => setAdding(false)} />}
      {palette && <Palette onClose={() => setPalette(false)} />}
    </div>
  );
}

/** The trial or plan line: days left in the last week, and a clear note once the workspace is read-only. */
function PlanBanner() {
  const app = useApp();
  const t = useT();
  const w = app.me.workspace;
  if (app.me.demo || w.plan === 'internal' || w.daysLeft === null) return null;
  const owner = app.caps.settings;
  const pay = owner ? <Link to="/settings/plan">{t('plan.pay')} →</Link> : <span>{t('plan.askOwner')}</span>;
  if (w.readOnly)
    return (
      <div className="plan-banner ended" role="status">
        <Icon name="warn" size={16} />
        <span className="grow">{w.status === 'trial' ? t('plan.ended') : t('plan.endedPaid')}</span>
        {pay}
      </div>
    );
  if (w.daysLeft > 7) return null;
  const text = w.status === 'trial' ? (w.daysLeft <= 0 ? t('plan.trialLast') : t('plan.trialLeft', { n: w.daysLeft })) : t('plan.paidLeft', { n: w.daysLeft });
  return (
    <div className="plan-banner" role="status">
      <Icon name="info" size={16} />
      <span className="grow">{text}</span>
      {owner && <Link to="/settings/plan">{t('plan.pay')} →</Link>}
    </div>
  );
}

// ---------------------------------------------------------------- side menu (WebSidebar board)

function Sidebar({ collapsed, onClose }: { collapsed: boolean; onClose: () => void }) {
  const app = useApp();
  const t = useT();
  const loc = useLocation();
  const menus = visibleMenus(app.caps);
  const active = menus.find((m) => m.items.some((i) => loc.pathname === i.path || loc.pathname.startsWith(i.path + '/')))?.id ?? (loc.pathname.startsWith('/m/owner') ? 'home' : '');
  const [open, setOpen] = useState(active);
  useEffect(() => setOpen(active), [active]);
  return (
    <nav className="sidebar" aria-label={t('nav.main')}>
      <Link className="sb-brand" to={landingFor(app.me.role, app.isPhone)} aria-label={t('nav.home.link')}>
        <BrandTile size={34} />
        {!collapsed && <Wordmark width={104} className="sb-word" />}
        {app.isPhone && (
          <button type="button" className="tb-btn" style={{ marginInlineStart: 'auto', color: '#fff' }} aria-label={t('nav.closeMenu')} onClick={(e) => { e.preventDefault(); onClose(); }}>
            <Icon name="x" />
          </button>
        )}
      </Link>
      <div className="sb-scroll">
        {menus.map((m) => (
          <Group key={m.id} menu={m} collapsed={collapsed} active={active === m.id} open={!collapsed && open === m.id} onToggle={() => setOpen(open === m.id ? '' : m.id)} />
        ))}
      </div>
      <div className="sb-foot">
        {(app.caps.approve || app.caps.owner) && (
          <NavLink to="/approvals" className={({ isActive }) => `sb-approvals${isActive ? ' active' : ''}`} title={t('nav.approvals')}>
            <Icon name="approvals" />
            {!collapsed && <span>{t('nav.approvals')}</span>}
            {app.approvals > 0 && <span className="sb-count">{app.approvals}</span>}
          </NavLink>
        )}
        {!collapsed && <div className="sb-by">{t('nav.by')}</div>}
      </div>
    </nav>
  );
}

function Group({ menu, collapsed, active, open, onToggle }: { menu: Menu; collapsed: boolean; active: boolean; open: boolean; onToggle: () => void }) {
  const t = useT();
  const first = menu.items[0]!;
  if (collapsed)
    return (
      <Link to={first.path} className={`sb-menu sb-icon-only${active ? ' active' : ''}`} title={t(menu.label)} aria-label={t(menu.label)}>
        <Icon name={menu.icon} />
      </Link>
    );
  return (
    <div className="sb-group">
      <button type="button" className={`sb-menu${active ? ' active' : ''}`} aria-expanded={open} onClick={onToggle}>
        <Icon name={menu.icon} />
        <span className="sb-text">{t(menu.label)}</span>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={14} stroke={2} className="sb-chev" />
      </button>
      {open && (
        <ul className="sb-items">
          {menu.items.map((i) => (
            <li key={i.id}>
              <NavLink to={i.path} end={false}>
                {t(i.label)}
                {i.later && <span className="sb-later">{t('nav.later').toUpperCase()}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- top bar (WebTopbar board)

function useCrumbs(): [string | null, string] {
  const t = useT();
  const loc = useLocation();
  if (loc.pathname.startsWith('/approvals')) return [null, t('nav.approvals')];
  if (loc.pathname.startsWith('/m/owner')) return [t('nav.home'), t('nav.home.weekly')];
  const f = findItem(loc.pathname);
  if (f) return [t(f.menu.label), t(f.item.label)];
  return [null, 'Buildiary'];
}

function ProjectMenu({ variant }: { variant: 'top' | 'phone' }) {
  const app = useApp();
  const t = useT();
  const loc = useLocation();
  const toast = useToast();
  const [sheet, setSheet] = useState(false);
  const all = loc.pathname.startsWith('/home/overview');
  const p = app.project;
  const label = all ? t('top.projects') : t('top.project');
  const value = all ? t('top.allProjects', { n: app.me.projects.length }) : p ? `${p.name} · ${p.place}` : '—';
  const pick = async (id: string) => {
    try {
      await app.setProject(id);
    } catch {
      toast(t('c.error'), 'error');
    }
  };
  const list = (close: () => void) => (
    <div className="dd-list" role="menu" aria-label={t('top.switchProject')}>
      {app.me.projects.map((x) => (
        <button key={x.id} type="button" role="menuitemradio" aria-checked={x.id === app.projectId} className="dd-item" onClick={() => { close(); void pick(x.id); }}>
          <Icon name="building" size={16} />
          <span className="stack grow" style={{ gap: 0 }}>
            <span>{x.name}</span>
            <span className="muted xsmall">{x.place} · {x.currency} · {x.stage}</span>
          </span>
          {x.id === app.projectId && <Icon name="tick" size={16} stroke={2.2} />}
        </button>
      ))}
    </div>
  );
  if (variant === 'phone')
    return (
      <>
        <button type="button" className="proj-btn" onClick={() => setSheet(true)} aria-haspopup="dialog">
          <Icon name="building" size={18} style={{ color: 'var(--brand-700)' }} />
          <span className="stack" style={{ gap: 0, lineHeight: 1.15 }}>
            <span className="l">{t('top.project')}</span>
            <span className="t ellipsis">{p?.name ?? '—'}</span>
          </span>
          <Icon name="chevronDown" size={14} stroke={2} style={{ marginInlineStart: 'auto', color: 'var(--ink-5)' }} />
        </button>
        {sheet && <Sheet title={t('top.switchProject')} onClose={() => setSheet(false)}>{list(() => setSheet(false))}</Sheet>}
      </>
    );
  return (
    <Dropdown
      label={`${t('top.switchProject')}: ${value}`}
      className="ctx"
      align="start"
      button={
        <>
          <Icon name="building" size={16} style={{ color: 'var(--brand-700)' }} />
          <span className="ctx-text">
            <span className="ctx-label">{label}</span>
            <span className="ctx-value">{value}</span>
          </span>
          <Icon name="chevronDown" size={12} stroke={2} style={{ color: 'var(--ink-5)' }} />
        </>
      }
    >
      {list}
    </Dropdown>
  );
}

function TopBar({ collapsed, onCollapse, onSearch }: { collapsed: boolean; onCollapse: () => void; onSearch: () => void }) {
  const app = useApp();
  const t = useT();
  const { lang } = useLang();
  const [c1, c2] = useCrumbs();
  return (
    <header className="topbar">
      <button type="button" className="tb-btn" aria-label={t(collapsed ? 'nav.expand' : 'nav.collapse')} onClick={onCollapse}>
        <Icon name="panel" className="flip-rtl" />
      </button>
      <nav className="crumbs" aria-label={t('nav.breadcrumb')}>
        {c1 && <span className="c1 only-wide">{c1}</span>}
        {c1 && <Icon name="chevronRight" size={14} stroke={2} className="sep flip-rtl only-wide" />}
        <span className="c2" aria-current="page">{c2}</span>
      </nav>
      <button type="button" className="search-trigger" onClick={onSearch} aria-keyshortcuts="Control+K Meta+K">
        <Icon name="search" size={16} stroke={2} />
        <span className="grow">{t('top.search')}</span>
        <kbd>Ctrl K</kbd>
      </button>
      <ProjectMenu variant="top" />
      <div className="ctx only-xwide" title={t('top.siteDate')}>
        <Icon name="calendar" size={16} style={{ color: 'var(--brand-700)' }} />
        <span className="ctx-text">
          <span className="ctx-label">{t('top.siteDate')}</span>
          <span className="ctx-value">{longDay(lang)}</span>
        </span>
      </div>
      <div className="tb-right">
        {(app.caps.approve || app.caps.owner) && (
          <Link to="/approvals" className="tb-btn" aria-label={t('top.approvals', { n: app.approvals })}>
            <Icon name="approvals" />
            {app.approvals > 0 && <span className="tb-badge">{app.approvals}</span>}
          </Link>
        )}
        <Notifications />
        <LangSeg />
        <AccountMenu />
      </div>
    </header>
  );
}

export function LangSeg({ className }: { className?: string }) {
  const app = useApp();
  const { lang, setLang } = useLang();
  const t = useT();
  const change = (l: Lang) => {
    setLang(l);
    request('POST /api/me/lang', { body: { lang: l } }).then((me) => app.setMe(me), () => undefined);
  };
  return (
    <div className={`lang-seg${className ? ' ' + className : ''}`} role="group" aria-label={t('top.language')}>
      {LANGS.map((l) => (
        <button key={l} type="button" lang={l} className={`l-${l}`} aria-pressed={lang === l} onClick={() => change(l)}>
          {LANG_NAME[l]}
        </button>
      ))}
    </div>
  );
}

function Notifications() {
  const app = useApp();
  const t = useT();
  const today = new Date();
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const notes = useAsync(() => (app.caps.site || app.caps.owner || app.caps.approve ? request('GET /api/diary', { query: { projectId: app.projectId, date: iso } }) : Promise.resolve([] as DiaryEntry[])), [app.version, app.projectId]);
  const list = notes.data ?? [];
  return (
    <Dropdown
      label={t('top.notifications', { n: list.length })}
      button={
        <>
          <Icon name="bell" />
          {list.length > 0 && <span className="tb-badge">{list.length}</span>}
        </>
      }
    >
      {(close) => (
        <div style={{ width: 360, maxWidth: 'calc(100vw - 32px)' }}>
          <div className="dd-head" style={{ justifyContent: 'space-between' }}>
            <strong>{t('top.notesTitle')}</strong>
            <span className="muted small">{app.project?.name}</span>
          </div>
          {list.length === 0 && <div className="dd-empty">{t('top.noNotes')}</div>}
          <div className="dd-list">
            {list.map((n) => (
              <Link key={n.id} to="/projects/diary" className="dd-item" onClick={close} style={{ alignItems: 'flex-start' }}>
                <span className="icon-chip sm tone-brand"><Icon name={n.kind === 'voice' ? 'mic' : n.kind === 'photo' ? 'camera' : n.kind === 'auto' ? 'bell' : 'doc'} size={15} /></span>
                <span className="stack grow" style={{ gap: 1 }}>
                  <span className="small" style={{ fontWeight: 500 }}>{n.text || n.transcript || 'Voice note'}</span>
                  <span className="muted xsmall"><span className="mono">{time(n.at)}</span> · {n.by}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Dropdown>
  );
}

function AccountMenu({ compact }: { compact?: boolean }) {
  const app = useApp();
  const t = useT();
  const { lang } = useLang();
  const toast = useToast();
  const [pin, setPin] = useState(false);
  const [sp, setSp] = useSearchParams();
  const [fb, setFb] = useState(() => sp.get('feedback') === '1');
  useEffect(() => {
    if (sp.get('feedback') === '1') {
      setFb(true);
      sp.delete('feedback');
      setSp(sp, { replace: true });
    }
  }, [sp, setSp]);
  const office = canOpenPath(app.caps, '/gate');
  return (
    <>
      <Dropdown
        label={t('top.account')}
        className="avatar-btn"
        button={
          <>
            <Avatar initials={app.me.user.initials} size={compact ? 40 : 36} />
            {!compact && <Icon name="chevronDown" size={12} stroke={2} />}
          </>
        }
      >
        {(close) => (
          <div style={{ minWidth: 270 }}>
            <div className="dd-head">
              <Avatar initials={app.me.user.initials} size={40} />
              <div className="stack" style={{ gap: 1 }}>
                <strong>{app.me.user.name}</strong>
                <span className="muted small">{ROLE_LABEL[lang][app.me.role]} · {app.me.workspace.name}</span>
                {app.me.demo && <span className="chip sm tone-brand" style={{ alignSelf: 'flex-start' }}>{t('top.demo')}</span>}
              </div>
            </div>
            {app.isPhone && (
              <div style={{ padding: '4px 6px 8px' }}>
                <LangSeg />
              </div>
            )}
            {office && (
              <Link to="/gate" className="dd-item" onClick={close}>
                <Icon name="gate" size={16} />
                {t('top.gateMode')}
              </Link>
            )}
            <button type="button" className="dd-item" onClick={() => { close(); setPin(true); }}>
              <Icon name="key" size={16} />
              {t('top.changePin')}
            </button>
            <button type="button" className="dd-item" onClick={() => { close(); setFb(true); }}>
              <Icon name="chat" size={16} />
              {t('top.feedback')}
            </button>
            {isNative() && (
              <button type="button" className="dd-item" onClick={() => { close(); nativeForget(); }}>
                <Icon name="external" size={16} />
                {t('top.changeWorkspace')}
              </button>
            )}
            <button type="button" className="dd-item" onClick={() => { close(); void app.signOut(); }}>
              <Icon name="logout" size={16} className="flip-rtl" />
              {t('top.signOut')}
            </button>
          </div>
        )}
      </Dropdown>
      {pin && (
        <FormDialog
          title={t('top.changePin')}
          sub="Site staff use the PIN with their site card at the gate and on shared tablets."
          fields={[
            { name: 'oldPin', label: 'Current PIN (if you have one)', type: 'password', inputMode: 'numeric' },
            { name: 'newPin', label: 'New PIN, 4 to 6 digits', type: 'password', inputMode: 'numeric', required: true },
          ]}
          submitLabel={t('c.save')}
          onClose={() => setPin(false)}
          onSubmit={async (v) => {
            await request('POST /api/me/pin', { body: { oldPin: s(v.oldPin) || undefined, newPin: s(v.newPin) } });
            setPin(false);
            toast(t('c.saved'));
          }}
        />
      )}
      {fb && (
        <FormDialog
          title={t('top.feedback')}
          sub="What works, what does not, what you miss. The SMH Global team reads every message."
          fields={[{ name: 'text', label: 'Your message', type: 'textarea', required: true }]}
          submitLabel={t('c.send')}
          onClose={() => setFb(false)}
          onSubmit={async (v) => {
            await request('POST /api/feedback', { body: { text: s(v.text) } });
            setFb(false);
            toast('Thank you. Your message was sent.');
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------- phone header, tab bar and the + sheet

function PhoneBar({ onMenu, onSearch }: { onMenu: () => void; onSearch: () => void }) {
  const t = useT();
  return (
    <header className="phone-head">
      <button type="button" className="round-btn" aria-label={t('nav.openMenu')} onClick={onMenu} style={{ marginInlineStart: -8 }}>
        <Icon name="menu" size={22} />
      </button>
      <ProjectMenu variant="phone" />
      <button type="button" className="round-btn" aria-label={t('top.search')} onClick={onSearch}>
        <Icon name="search" size={20} />
      </button>
      <AccountMenu compact />
    </header>
  );
}

function TabBar({ onAdd, onMore }: { onAdd: () => void; onMore: () => void }) {
  const app = useApp();
  const t = useT();
  const loc = useLocation();
  const todayPath = app.me.role === 'owner' ? '/m/owner' : '/home/today';
  const tab = (to: string, icon: IconName, label: string, badge?: number) => {
    const on = loc.pathname === to;
    return (
      <Link to={to} className="tab" aria-current={on ? 'page' : undefined}>
        <Icon name={icon} size={24} stroke={1.9} />
        <span>{label}</span>
        {badge ? <span className="tb-badge">{badge}</span> : null}
      </Link>
    );
  };
  return (
    <nav className="tabbar" aria-label="Tabs">
      {tab(todayPath, 'home', t('m.today'))}
      {tab('/scan', 'scan', t('m.scan'))}
      <button type="button" className="tab-plus" aria-label={t('m.add')} onClick={onAdd}>
        <Icon name="plus" size={26} stroke={2.4} />
      </button>
      {tab('/approvals', 'inbox', t('m.inbox'), app.approvals)}
      <button type="button" className="tab" onClick={onMore} aria-haspopup="dialog">
        <Icon name="menu" size={24} stroke={1.9} />
        <span>{t('m.more')}</span>
      </button>
    </nav>
  );
}

type AddAction = { key: 'add.receive' | 'add.issue' | 'add.water' | 'add.debris' | 'add.diary' | 'add.photo' | 'add.bill' | 'add.petty'; to: string; icon: IconName; accent?: boolean };
export const ADD_ACTIONS: AddAction[] = [
  { key: 'add.receive', to: '/stores/receive', icon: 'truck' },
  { key: 'add.issue', to: '/stores/issue?add=1', icon: 'issue' },
  { key: 'add.water', to: '/stores/water?add=1', icon: 'drop' },
  { key: 'add.debris', to: '/stores/debris?add=1', icon: 'truck' },
  { key: 'add.diary', to: '/projects/diary', icon: 'mic', accent: true },
  { key: 'add.photo', to: '/projects/photos?add=1', icon: 'camera' },
  { key: 'add.bill', to: '/money/bills?add=1', icon: 'bill', accent: true },
  { key: 'add.petty', to: '/money/petty-cash?add=1', icon: 'wallet' },
];

function AddSheet({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const t = useT();
  const nav = useNavigate();
  const acts = ADD_ACTIONS.filter((a) => canOpenPath(app.caps, a.to.split('?')[0]!));
  return (
    <Sheet title={t('m.addTitle')} onClose={onClose}>
      <div className="add-grid">
        {acts.map((a) => (
          <button key={a.key} type="button" className="add-tile" onClick={() => { onClose(); nav(a.to); }}>
            <span className={`icon-chip sm ${a.accent ? 'tone-brand' : 'tone-neutral'}`}><Icon name={a.icon} size={16} /></span>
            {t(a.key)}
          </button>
        ))}
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------- search or jump to (Ctrl K)

type Cmd = { id: string; label: string; hint?: string; icon: IconName; group: string; to: string };

function Palette({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const t = useT();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const [found, setFound] = useState<Cmd[]>([]);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  useEffect(() => input.current?.focus(), []);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setFound([]);
      return;
    }
    let live = true;
    setBusy(true);
    const h = setTimeout(() => {
      request('GET /api/search', { query: { q: term } })
        .then((rs) => {
          if (!live) return;
          const icon: Record<string, IconName> = { worker: 'person', item: 'box', supplier: 'cart', change_order: 'doc', order: 'cart', bill: 'bill', project: 'building', member: 'person' };
          setFound(rs.map((r) => ({ id: `${r.type}-${r.id}`, label: r.title, hint: r.sub, icon: icon[r.type] ?? 'file', group: t('pal.found'), to: toWebHref(r.href, r.title) ?? '/' })));
        })
        .catch(() => live && setFound([]))
        .finally(() => live && setBusy(false));
    }, 180);
    return () => {
      live = false;
      clearTimeout(h);
    };
  }, [q, t]);
  const pages = useMemo<Cmd[]>(
    () => [
      ...(app.caps.approve || app.caps.owner ? [{ id: 'approvals', label: t('nav.approvals'), icon: 'approvals' as IconName, group: t('pal.goTo'), to: '/approvals' }] : []),
      ...visibleMenus(app.caps).flatMap((m) => m.items.map((i) => ({ id: `${m.id}-${i.id}`, label: t(i.label), hint: t(m.label), icon: m.icon, group: t('pal.goTo'), to: i.path }))),
    ],
    [app.caps, t],
  );
  const term = q.trim().toLowerCase();
  const matched = term ? pages.filter((c) => `${c.label} ${c.hint ?? ''}`.toLowerCase().includes(term)) : pages.slice(0, 10);
  const all = [...found, ...matched];
  const safe = Math.min(idx, Math.max(0, all.length - 1));
  useEffect(() => setIdx(0), [q]);
  const go = (c: Cmd | undefined) => {
    if (!c) return;
    onClose();
    nav(c.to);
  };
  let last = '';
  return (
    <div className="overlay top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="palette" role="dialog" aria-modal="true" aria-label={t('pal.title')}>
        <div className="palette-input">
          <Icon name="search" />
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('pal.placeholder')}
            aria-label={t('pal.title')}
            role="combobox"
            aria-expanded="true"
            aria-controls="pal-list"
            aria-activedescendant={all[safe] ? `pal-${safe}` : undefined}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(all.length - 1, i + 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
              else if (e.key === 'Enter') { e.preventDefault(); go(all[safe]); }
              else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            }}
          />
          {busy ? <span className="spinner" /> : <kbd>Esc</kbd>}
        </div>
        <ul className="palette-list" id="pal-list" role="listbox" aria-label={t('pal.title')}>
          {all.length === 0 && <li className="palette-empty">{busy ? t('pal.searching') : t('pal.none')}</li>}
          {all.map((c, i) => {
            const head = c.group !== last ? c.group : null;
            last = c.group;
            return (
              <li key={c.id} role="presentation">
                {head && <div className="palette-group">{head}</div>}
                <div id={`pal-${i}`} role="option" aria-selected={i === safe} className="palette-item" onMouseEnter={() => setIdx(i)} onClick={() => go(c)}>
                  <Icon name={c.icon} size={16} />
                  <span className="ellipsis">{c.label}</span>
                  {c.hint && <span className="hint ellipsis">{c.hint}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/** Wraps a page: shows "no access" when the person's caps do not allow the path (the server checks too). */
export function Guard({ children }: { children: ReactNode }) {
  const app = useApp();
  const t = useT();
  const loc = useLocation();
  if (!canOpenPath(app.caps, loc.pathname))
    return (
      <div className="empty" style={{ paddingTop: 80 }}>
        <span className="icon-chip tone-neutral"><Icon name="lock" /></span>
        <h3>{t('c.noAccess')}</h3>
        <Link className="btn" to={landingFor(app.me.role, app.isPhone)}>{t('c.goHome')}</Link>
      </div>
    );
  return <>{children}</>;
}
